import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { APIMailService, APIMetaService } from '@takectrl-multiapp/svcs-ctrl-api';
import { firstValueFrom } from 'rxjs';
import {
  IClaimMailPayload,
  IEmergencyMailPayload,
  IHolidayViolationMailPayload,
  IInsurersDetails,
  IMandateViolationMailPayload,
  SharedConfig,
  SharedDialogService,
} from '@takectrl-multiapp/svcs-share-config';
import { ICtrlResponse } from 'libs/svcs-ctrl-api/src/lib/interfaces/api.interface';
import { Platform } from '@ionic/angular';
import { CommunicationService } from '@takectrl-multiapp/svcs-core';

@Injectable({
  providedIn: 'root',
})
export class BUSNCommunicationService {
  constructor(
    public sharedConfig: SharedConfig,
    public communicationService: CommunicationService,
    private platform: Platform,
    private readonly apiMailService: APIMailService,
    public angularFireDatabase: AngularFireDatabase,
    private readonly apiMetaService: APIMetaService,
    public sharedDialogService: SharedDialogService
  ) {}

  // Email methods remain unchanged
  async sendMandateViolationEmail(quotes_id: number): Promise<ICtrlResponse<unknown>> {
    console.log('Sending mandate violation email');
    const payload: IMandateViolationMailPayload = {
      quotes_id,
    };
    return await firstValueFrom(this.apiMailService.mandateViolationEmail(payload));
  }

  async openContactCardPopup() {
    if (this.sharedConfig.userPermissions?.phone_insurer) {
      SharedConfig.showCustomLoader = true;
      let results = (await firstValueFrom(await this.apiMetaService.getActiveInsurerDetails())).results
      this.sharedConfig.appClientDetail.insurer_contact_details = results;
      SharedConfig.showCustomLoader = false;
    }
    SharedConfig.popupDialogContactCard = true;
    this.sharedDialogService.showPopupDialog({
      header: 'Contact us via',
      subHeader: 'Select one of the contact methods below.',
      contactList: true,
      needToScroll: this.sharedConfig.appClientDetail.insurer_contact_details && this.sharedConfig.appClientDetail.insurer_contact_details.length > 3 ? true : false
    });
  }

  async sendHolidayViolationEmail(quotes_id: number): Promise<ICtrlResponse<unknown>> {
    console.log('Sending holiday violation email');
    const payload: IHolidayViolationMailPayload = {
      quotes_id,
    };
    return await firstValueFrom(this.apiMailService.holidayViolationEmail(payload));
  }

  async sendClaimEmail(emailPayload: IClaimMailPayload): Promise<any> {
    console.log('Sending claims email');
    const claimsResult = await firstValueFrom(this.apiMailService.newClaimEmail(emailPayload));
    return claimsResult;
  }

  async sendBrokerEmail(emailPayload: IEmergencyMailPayload): Promise<any> {
    const brokerResult = await firstValueFrom(this.apiMailService.sendBrokerEmergencyEmail(emailPayload));
    return brokerResult;
  }

  // Updated phone methods
  private handlePhoneCall(phoneNumber: string, contactName?: string) {
    if (this.platform.is('desktop')) {
      // Show dialog for desktop
      this.sharedDialogService.showPopupDialog({
        header: contactName ? `${contactName}'s number:` : 'Contact number:',
        subHeader: phoneNumber,
        buttonOneText: 'OK',
        buttonOneCallback: this.sharedDialogService.hidePopupDialog,
      });
    } else {
      // For mobile web and PWA
      this.communicationService.makePhoneCall(phoneNumber);
    }
  }

  callBroker(): void {
    const brokerPhone = this.sharedConfig.user.broker.telephone;
    const brokerName = this.sharedConfig.user.broker.contact;
    this.handlePhoneCall(brokerPhone, brokerName);
  }

  async getInsurersAndShowPopup() {
    try {
      const response = await firstValueFrom(await this.apiMetaService.getActiveInsurerDetails());
      this.sharedConfig.appClientDetail.insurer_contact_details = response.results;
      this.sharedDialogService.showPopupDialog({
        header: 'Call Insurer',
        subHeader: 'Choose the insurer you would like to contact',
        callInsurerPopup: true,
      });
    } catch (error) {
      console.error('Error fetching insurer details:', error);
      this.sharedDialogService.showPopupDialog({
        header: 'Error',
        subHeader: 'Unable to fetch insurer details',
        buttonOneText: 'OK',
        buttonOneCallback: this.sharedDialogService.hidePopupDialog,
      });
    }
  }

  callInsurer(insurer: IInsurersDetails) {
    this.sharedDialogService.hidePopupDialog();
    SharedConfig.showCustomLoader = true;

    // Using setTimeout instead of setInterval since we only need to run this once
    setTimeout(() => {
      try {
        this.handlePhoneCall(insurer.contact_number, insurer.description);
      } catch (error) {
        console.error('Error making phone call:', error);
        this.sharedDialogService.showPopupDialog({
          header: 'Error',
          subHeader: 'Unable to initiate call',
          buttonOneText: 'OK',
          buttonOneCallback: this.sharedDialogService.hidePopupDialog,
        });
      } finally {
        SharedConfig.showCustomLoader = false;
      }
    }, 500);
  }
}
